/**
 * Shared input rules + Vietnamese messages for every form. Mirrors the API's `Platform/InputRules.cs`
 * (phone, email, URL) and `Validator` messages — change both together. The server stays authoritative;
 * these give the same answer earlier.
 *
 * Two ways to use them:
 *  - react-hook-form: build a `z.object({...})` from the helpers below + `zodResolver`, submit with
 *    `form.handleSubmit(onValid, focusFirstInvalid)`; register text fields with `mode: 'onTouched'` for blur feedback.
 *  - useState forms: `const r = validate(schema, values)` → `r.ok ? r.data : setErrors(r.errors)` then `focusFirstInvalid()`.
 * Server 422s: `applyFieldErrors` (RHF) or `serverFieldErrors` (useState) from `lib/forms`.
 */
import { z } from 'zod';
import { isDecimalString, compareDecimal } from './money';
import { todayVn } from './dates';

// ───────────────────────── messages ─────────────────────────

export const MSG = {
  required: (label?: string) => (label ? `${label} là bắt buộc.` : 'Trường này là bắt buộc.'),
  choose: (label?: string) => (label ? `Chọn ${label.toLowerCase()}.` : 'Hãy chọn một giá trị.'),
  maxLength: (max: number, label?: string) => (label ? `${label} tối đa ${max} ký tự.` : `Tối đa ${max} ký tự.`),
  minLength: (min: number, label?: string) => (label ? `${label} tối thiểu ${min} ký tự.` : `Tối thiểu ${min} ký tự.`),
  phone: (label = 'Số điện thoại') => `${label} không hợp lệ (10–11 số, bắt đầu bằng 0 hoặc +84).`,
  email: (label = 'Email') => `${label} không hợp lệ.`,
  url: (label = 'Đường dẫn') => `${label} phải là địa chỉ http(s) hợp lệ.`,
  number: (label?: string) => (label ? `${label} phải là số.` : 'Giá trị phải là số.'),
  integer: (label?: string) => (label ? `${label} phải là số nguyên.` : 'Giá trị phải là số nguyên.'),
  nonNegative: (label?: string) => (label ? `${label} không được âm.` : 'Giá trị không được âm.'),
  positive: (label?: string) => (label ? `${label} phải lớn hơn 0.` : 'Giá trị phải lớn hơn 0.'),
  min: (min: string | number, label?: string) => (label ? `${label} tối thiểu ${min}.` : `Tối thiểu ${min}.`),
  max: (max: string | number, label?: string) => (label ? `${label} tối đa ${max}.` : `Tối đa ${max}.`),
  percent: (label?: string) => (label ? `${label} phải từ 0 đến 100.` : 'Giá trị phải từ 0 đến 100.'),
  scale: (decimals: number, currency?: string) =>
    decimals === 0 ? `${currency ?? 'Số tiền'} không có phần lẻ thập phân.` : `Tối đa ${decimals} chữ số thập phân.`,
  date: (label?: string) => (label ? `${label} không hợp lệ.` : 'Ngày không hợp lệ.'),
  notPast: (label?: string) => (label ? `${label} không được ở quá khứ.` : 'Không chọn ngày trong quá khứ.'),
  notFuture: (label?: string) => (label ? `${label} không được ở tương lai.` : 'Không chọn ngày trong tương lai.'),
  dateOrder: 'Ngày kết thúc phải sau hoặc bằng ngày bắt đầu.',
  dateTimeOrder: 'Thời điểm kết thúc phải sau thời điểm bắt đầu.',
  phoneOrEmail: 'Cần SĐT hoặc email.',
  code: (label = 'Mã') => `${label} chỉ gồm chữ in hoa A–Z, số 0–9, "-" hoặc "_".`,
  fileSize: (mb: number) => `Tệp vượt quá ${mb}MB.`,
  fileType: 'Định dạng tệp không được hỗ trợ.',
  fileEmpty: 'Tệp rỗng.',
} as const;

// ───────────────────────── pure rules (mirror InputRules.cs) ─────────────────────────

export const EMAIL_MAX = 254;
export const URL_MAX = 500;

/**
 * Vietnamese phone → national form with a leading 0 ("+84 912.345.678" → "0912345678"). Accepts spaces, dots,
 * dashes, parentheses and a +84/84 prefix. Valid = 10–11 digits starting with 0. Null when blank or invalid.
 */
export function normalizePhoneVn(input: string | null | undefined): string | null {
  const t = (input ?? '').trim();
  if (!t) return null;
  if (!/^[\d .\-()+]+$/.test(t)) return null;
  if (t.indexOf('+') > 0 || (t.match(/\+/g)?.length ?? 0) > 1) return null;
  let d = t.replace(/\D/g, '');
  if (t.startsWith('+') && !d.startsWith('84')) return null;
  if (d.startsWith('84') && d.length >= 11) d = d[2] === '0' ? d.slice(2) : `0${d.slice(2)}`;
  else if (d.length === 9 && !d.startsWith('0')) d = `0${d}`;
  return d.length >= 10 && d.length <= 11 && d.startsWith('0') ? d : null;
}

export const isPhoneVn = (s: string | null | undefined) => normalizePhoneVn(s) !== null;

const EMAIL_RE = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;

/** Trimmed lower-case email ('' when blank). */
export function normalizeEmail(s: string | null | undefined): string {
  return (s ?? '').trim().toLowerCase();
}

/** Same as InputRules.IsEmail: one "@", dotted domain without empty labels, no spaces, ≤ 254 chars. */
export function isEmail(s: string | null | undefined): boolean {
  return !!s && s.length <= EMAIL_MAX && EMAIL_RE.test(s);
}

export function isHttpUrl(s: string | null | undefined): boolean {
  const t = (s ?? '').trim();
  if (!t || t.length > URL_MAX) return false;
  try {
    const u = new URL(t);
    return (u.protocol === 'http:' || u.protocol === 'https:') && !!u.hostname;
  } catch {
    return false;
  }
}

/** Real calendar date in `yyyy-MM-dd` form (rejects 2026-02-30). */
export function isDateOnly(s: string | null | undefined): boolean {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  if (y < 1900 || y > 2999) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** Shift a DateOnly by whole years (Feb 29 → Feb 28). */
export function addYears(date: string, years: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y + years, m - 1, d));
  if (dt.getUTCMonth() !== m - 1) dt.setUTCDate(0);
  return dt.toISOString().slice(0, 10);
}

/** Decimal places used by each currency (mirror of MoneyPolicy: VND/JPY/KRW 0, others 2). */
export function currencyScale(currency = 'VND'): number {
  return ['VND', 'JPY', 'KRW'].includes(currency.toUpperCase()) ? 0 : 2;
}

function fractionDigits(v: string): number {
  return (v.split('.')[1] ?? '').replace(/0+$/, '').length;
}

function intDigits(v: string): number {
  return v.replace(/^-/, '').split('.')[0]!.replace(/^0+(?=\d)/, '').length;
}

// ───────────────────────── zod builders ─────────────────────────

type Opt = { label?: string };
type Req = Opt & { required?: boolean };

/** Required text: trimmed, whitespace-only = empty, ≤ max. Output = trimmed string. */
export function requiredText(max: number, opts: Opt & { min?: number } = {}) {
  let s = z.string({ error: MSG.required(opts.label) }).trim().min(1, MSG.required(opts.label));
  if (opts.min && opts.min > 1) s = s.min(opts.min, MSG.minLength(opts.min, opts.label));
  return s.max(max, MSG.maxLength(max, opts.label));
}

/** Optional text: trimmed, ≤ max. Output = trimmed string ('' when blank). Accepts null/undefined. */
export function optionalText(max: number, opts: Opt = {}) {
  return z
    .string()
    .nullish()
    .transform((v) => (v ?? '').trim())
    .pipe(z.string().max(max, MSG.maxLength(max, opts.label)));
}

/** Vietnamese phone. Output = normalized "0xxxxxxxxx" or '' when blank and optional. */
export function phoneVn(opts: Req = {}) {
  return z
    .string()
    .nullish()
    .transform((v) => (v ?? '').trim())
    .superRefine((v, ctx) => {
      if (!v) {
        if (opts.required) ctx.addIssue({ code: 'custom', message: MSG.required(opts.label ?? 'Số điện thoại') });
        return;
      }
      if (!isPhoneVn(v)) ctx.addIssue({ code: 'custom', message: MSG.phone(opts.label) });
    })
    .transform((v) => normalizePhoneVn(v) ?? '');
}

/** Email: trimmed + lower-cased. Output '' when blank and optional. */
export function email(opts: Req & { max?: number } = {}) {
  const max = opts.max ?? EMAIL_MAX;
  return z
    .string()
    .nullish()
    .transform((v) => normalizeEmail(v))
    .superRefine((v, ctx) => {
      if (!v) {
        if (opts.required) ctx.addIssue({ code: 'custom', message: MSG.required(opts.label ?? 'Email') });
        return;
      }
      if (v.length > max) ctx.addIssue({ code: 'custom', message: MSG.maxLength(max, opts.label ?? 'Email') });
      else if (!isEmail(v)) ctx.addIssue({ code: 'custom', message: MSG.email(opts.label) });
    });
}

export function url(opts: Req = {}) {
  return z
    .string()
    .nullish()
    .transform((v) => (v ?? '').trim())
    .superRefine((v, ctx) => {
      if (!v) {
        if (opts.required) ctx.addIssue({ code: 'custom', message: MSG.required(opts.label ?? 'Đường dẫn') });
        return;
      }
      if (!isHttpUrl(v)) ctx.addIssue({ code: 'custom', message: MSG.url(opts.label) });
    });
}

/**
 * Business code: trimmed + upper-cased, then matched against `pattern` (default `^[A-Z0-9_-]+$`).
 * Pass the server's exact pattern/limits.
 */
export function code(opts: Req & { max: number; min?: number; pattern?: RegExp; message?: string; upper?: boolean }) {
  const pattern = opts.pattern ?? /^[A-Z0-9_-]+$/;
  return z
    .string()
    .nullish()
    .transform((v) => {
      const t = (v ?? '').trim();
      return opts.upper === false ? t : t.toUpperCase();
    })
    .superRefine((v, ctx) => {
      if (!v) {
        if (opts.required !== false) ctx.addIssue({ code: 'custom', message: MSG.required(opts.label ?? 'Mã') });
        return;
      }
      if (v.length > opts.max) ctx.addIssue({ code: 'custom', message: MSG.maxLength(opts.max, opts.label ?? 'Mã') });
      else if (opts.min && v.length < opts.min) ctx.addIssue({ code: 'custom', message: MSG.minLength(opts.min, opts.label ?? 'Mã') });
      else if (!pattern.test(v)) ctx.addIssue({ code: 'custom', message: opts.message ?? MSG.code(opts.label) });
    });
}

export interface DecimalOpts extends Req {
  /** Inclusive bounds as decimal strings/numbers. Default min 0 (non-negative). */
  min?: string | number;
  max?: string | number;
  /** Strictly greater than 0. */
  positive?: boolean;
  /** Max fraction digits (default: currency scale for money, 4 otherwise). */
  scale?: number;
  /** Max integer digits (DB precision 19,4 → 15). */
  maxIntDigits?: number;
  allowNegative?: boolean;
}

function decimalRule(v: string, ctx: z.RefinementCtx, o: DecimalOpts & { scaleMsg: string }) {
  if (!isDecimalString(v)) return ctx.addIssue({ code: 'custom', message: MSG.number(o.label) });
  const min = o.min ?? (o.allowNegative ? undefined : 0);
  if (o.positive && compareDecimal(v, '0') <= 0) return ctx.addIssue({ code: 'custom', message: MSG.positive(o.label) });
  if (min !== undefined && compareDecimal(v, String(min)) < 0)
    return ctx.addIssue({ code: 'custom', message: String(min) === '0' ? MSG.nonNegative(o.label) : MSG.min(String(min), o.label) });
  if (o.max !== undefined && compareDecimal(v, String(o.max)) > 0) return ctx.addIssue({ code: 'custom', message: MSG.max(String(o.max), o.label) });
  if (o.scale !== undefined && fractionDigits(v) > o.scale) return ctx.addIssue({ code: 'custom', message: o.scaleMsg });
  const maxInt = o.maxIntDigits ?? 15;
  if (intDigits(v) > maxInt) return ctx.addIssue({ code: 'custom', message: MSG.max(`${maxInt} chữ số phần nguyên`, o.label) });
}

function decimalSchema(o: DecimalOpts & { scaleMsg: string }) {
  return z
    .string()
    .nullish()
    .transform((v) => (v ?? '').trim().replace(',', '.'))
    .superRefine((v, ctx) => {
      if (!v) {
        if (o.required) ctx.addIssue({ code: 'custom', message: MSG.required(o.label) });
        return;
      }
      decimalRule(v, ctx, o);
    });
}

/** Money as a decimal string (MoneyInput emits these). Non-negative by default; fraction digits per currency. */
export function money(opts: DecimalOpts & { currency?: string } = {}) {
  const scale = opts.scale ?? currencyScale(opts.currency);
  return decimalSchema({ ...opts, scale, scaleMsg: MSG.scale(scale, opts.currency) });
}

/** Generic decimal string (quantities, rates). Default 4 fraction digits, non-negative. */
export function decimal(opts: DecimalOpts = {}) {
  const scale = opts.scale ?? 4;
  return decimalSchema({ ...opts, scale, scaleMsg: MSG.scale(scale) });
}

/** Percentage 0–100 as a decimal string (default 4 fraction digits, like numeric(9,4)). */
export function percent(opts: Req & { scale?: number; positive?: boolean } = {}) {
  const scale = opts.scale ?? 4;
  return z
    .string()
    .nullish()
    .transform((v) => (v ?? '').trim().replace(',', '.'))
    .superRefine((v, ctx) => {
      if (!v) {
        if (opts.required) ctx.addIssue({ code: 'custom', message: MSG.required(opts.label) });
        return;
      }
      if (!isDecimalString(v)) return ctx.addIssue({ code: 'custom', message: MSG.number(opts.label) });
      if (compareDecimal(v, '0') < 0 || compareDecimal(v, '100') > 0) return ctx.addIssue({ code: 'custom', message: MSG.percent(opts.label) });
      if (opts.positive && compareDecimal(v, '0') === 0) return ctx.addIssue({ code: 'custom', message: MSG.positive(opts.label) });
      if (fractionDigits(v) > scale) ctx.addIssue({ code: 'custom', message: MSG.scale(scale) });
    });
}

/**
 * Whole number typed into a text/number input. Output: number, or null when blank and optional.
 * Bounds are inclusive; default min 0.
 */
export function integer(opts: Req & { min?: number; max?: number; positive?: boolean } = {}) {
  const min = opts.min ?? 0;
  return z
    .union([z.string(), z.number()])
    .nullish()
    .transform((v) => (v === null || v === undefined ? '' : String(v).trim()))
    .superRefine((v, ctx) => {
      if (!v) {
        if (opts.required) ctx.addIssue({ code: 'custom', message: MSG.required(opts.label) });
        return;
      }
      if (!/^-?\d+$/.test(v)) return ctx.addIssue({ code: 'custom', message: MSG.integer(opts.label) });
      const n = Number(v);
      if (opts.positive && n <= 0) ctx.addIssue({ code: 'custom', message: MSG.positive(opts.label) });
      else if (n < min) ctx.addIssue({ code: 'custom', message: min === 0 ? MSG.nonNegative(opts.label) : MSG.min(min, opts.label) });
      else if (opts.max !== undefined && n > opts.max) ctx.addIssue({ code: 'custom', message: MSG.max(opts.max, opts.label) });
    })
    .transform((v) => (v ? Number(v) : null));
}

export interface DateOpts extends Req {
  /** Not before today (Vietnam business date — same as the server's BusinessToday). */
  notPast?: boolean;
  notFuture?: boolean;
  /** Inclusive bounds `yyyy-MM-dd`. */
  min?: string;
  max?: string;
  minMessage?: string;
  maxMessage?: string;
  /** Injectable "today" for tests. */
  today?: () => string;
}

/** DateOnly "yyyy-MM-dd" (DateInput). Output '' when blank and optional. */
export function dateOnly(opts: DateOpts = {}) {
  return z
    .string()
    .nullish()
    .transform((v) => (v ?? '').trim())
    .superRefine((v, ctx) => {
      if (!v) {
        if (opts.required) ctx.addIssue({ code: 'custom', message: MSG.required(opts.label) });
        return;
      }
      if (!isDateOnly(v)) return ctx.addIssue({ code: 'custom', message: MSG.date(opts.label) });
      const today = (opts.today ?? todayVn)();
      if (opts.notPast && v < today) return ctx.addIssue({ code: 'custom', message: MSG.notPast(opts.label) });
      if (opts.notFuture && v > today) return ctx.addIssue({ code: 'custom', message: MSG.notFuture(opts.label) });
      if (opts.min && v < opts.min) return ctx.addIssue({ code: 'custom', message: opts.minMessage ?? MSG.min(fmtDate(opts.min), opts.label) });
      if (opts.max && v > opts.max) ctx.addIssue({ code: 'custom', message: opts.maxMessage ?? MSG.max(fmtDate(opts.max), opts.label) });
    });
}

/** Date of birth: real date, not in the future, age within [minAge, maxAge] (default 0–120). */
export function dateOfBirth(opts: Req & { minAge?: number; maxAge?: number; today?: () => string } = {}) {
  const today = (opts.today ?? todayVn)();
  const label = opts.label ?? 'Ngày sinh';
  return dateOnly({
    ...opts,
    label,
    notFuture: true,
    min: addYears(today, -(opts.maxAge ?? 120)),
    max: opts.minAge ? addYears(today, -opts.minAge) : undefined,
    minMessage: `${label} không hợp lệ (quá ${opts.maxAge ?? 120} tuổi).`,
    maxMessage: opts.minAge ? `Phải đủ ${opts.minAge} tuổi.` : undefined,
  });
}

/** `datetime-local` value ("yyyy-MM-ddTHH:mm[:ss]"). Output '' when blank and optional. */
export function dateTimeLocal(opts: Req = {}) {
  return z
    .string()
    .nullish()
    .transform((v) => (v ?? '').trim())
    .superRefine((v, ctx) => {
      if (!v) {
        if (opts.required) ctx.addIssue({ code: 'custom', message: MSG.required(opts.label) });
        return;
      }
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(v) || !isDateOnly(v.slice(0, 10)))
        ctx.addIssue({ code: 'custom', message: MSG.date(opts.label) });
    });
}

/** Required selection (Select/Combobox value: '' / null / undefined = not chosen). */
export function requiredChoice<T>(label?: string) {
  // Not `z.custom(fn)`: a failing custom check aborts the parent object, so cross-field refinements (dateRange…)
  // would stay silent until every picker is filled. A superRefine issue is continuable.
  return z.custom<T>().superRefine((v, ctx) => {
    if (v === null || v === undefined || v === '') ctx.addIssue({ code: 'custom', message: MSG.choose(label) });
  });
}

function fmtDate(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

// ───────────────────────── cross-field refinements ─────────────────────────

type Obj = Record<string, unknown>;

/**
 * `to` ≥ `from` for DateOnly/datetime-local strings (lexicographic order works for both). Error on `to`.
 * Use: `schema.superRefine(dateRange('startDate', 'endDate'))`. `strict` → `to` > `from`.
 */
export function dateRange<T extends Obj>(from: keyof T & string, to: keyof T & string, opts: { message?: string; strict?: boolean } = {}) {
  return (v: T, ctx: z.RefinementCtx) => {
    const a = v[from];
    const b = v[to];
    if (typeof a !== 'string' || typeof b !== 'string' || !a || !b) return;
    if (opts.strict ? b <= a : b < a) ctx.addIssue({ code: 'custom', path: [to], message: opts.message ?? (opts.strict ? MSG.dateTimeOrder : MSG.dateOrder) });
  };
}

/** At least one of `keys` must be non-blank; error on the first key (or `path`). */
export function atLeastOne<T extends Obj>(keys: (keyof T & string)[], message: string = MSG.phoneOrEmail, path?: keyof T & string) {
  return (v: T, ctx: z.RefinementCtx) => {
    const any = keys.some((k) => {
      const x = v[k];
      return typeof x === 'string' ? x.trim() !== '' : x !== null && x !== undefined;
    });
    if (!any) ctx.addIssue({ code: 'custom', path: [path ?? keys[0]!], message });
  };
}

/** Lead/contact rule: phone or email required (server: "Cần SĐT hoặc email.", keyed on phone). */
export const phoneOrEmail = <T extends Obj>(phone = 'phone' as keyof T & string, mail = 'email' as keyof T & string) =>
  atLeastOne<T>([phone, mail], MSG.phoneOrEmail, phone);

// ───────────────────────── files ─────────────────────────

export const DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;

/** Client pre-check of a file: empty, size (default 25 MB), and extension list (".pdf,.xlsx" or ["pdf","xlsx"]). */
export function checkFile(file: { name: string; size: number }, opts: { maxBytes?: number; accept?: string | string[] } = {}): string | null {
  if (file.size === 0) return MSG.fileEmpty;
  const max = opts.maxBytes ?? DOCUMENT_MAX_BYTES;
  if (file.size > max) return MSG.fileSize(Math.round(max / 1024 / 1024));
  if (opts.accept) {
    const list = (Array.isArray(opts.accept) ? opts.accept : opts.accept.split(','))
      .map((s) => s.trim().toLowerCase().replace(/^\./, ''))
      .filter(Boolean);
    const ext = file.name.toLowerCase().split('.').pop() ?? '';
    if (list.length && !list.includes(ext)) return MSG.fileType;
  }
  return null;
}

// ───────────────────────── running a schema outside react-hook-form ─────────────────────────

export type FieldErrors = Record<string, string>;

/**
 * Runs a schema on plain values (useState forms). Returns parsed data or a flat `{ "path.0.name": message }` map
 * (first message per path) — same keys as `serverFieldErrors`.
 */
export function validate<S extends z.ZodType>(schema: S, values: unknown): { ok: true; data: z.output<S> } | { ok: false; errors: FieldErrors } {
  const r = schema.safeParse(values);
  if (r.success) return { ok: true, data: r.data };
  return { ok: false, errors: issuesToErrors(r.error.issues) };
}

export function issuesToErrors(issues: readonly { path: readonly PropertyKey[]; message: string }[]): FieldErrors {
  const errors: FieldErrors = {};
  for (const i of issues) {
    const key = i.path.map(String).join('.') || '_form';
    if (!(key in errors)) errors[key] = i.message;
  }
  return errors;
}

/** Validate a single field (blur feedback in useState forms): returns its message or undefined. */
export function fieldError<S extends z.ZodType>(schema: S, values: unknown, path: string): string | undefined {
  const r = schema.safeParse(values);
  if (r.success) return undefined;
  return issuesToErrors(r.error.issues)[path];
}

/**
 * Focuses and scrolls to the first invalid control (`aria-invalid="true"`) inside `root` (default: the open dialog
 * or the document). Runs after the next paint so freshly rendered errors are found. Use as RHF `handleSubmit`'s
 * second argument or after `setErrors` in useState forms.
 */
export function focusFirstInvalid(root?: unknown): void {
  const run = () => {
    const given = root && typeof (root as ParentNode).querySelector === 'function' ? (root as ParentNode) : null;
    const dialogs = document.querySelectorAll('[role="dialog"]');
    const scope: ParentNode = given ?? dialogs[dialogs.length - 1] ?? document;
    const el = scope.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!el) return;
    el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    el.focus?.({ preventScroll: true });
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(run));
  else setTimeout(run, 0);
}
